// Auth context + hook.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, tokenStore, userStore } from '@/lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => userStore.get());
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      userStore.set(data);
    } catch {
      setUser(null);
      tokenStore.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tokenStore.get()) {
      refreshMe();
    } else {
      setLoading(false);
    }
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    tokenStore.set(data.access_token);
    userStore.set(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    tokenStore.clear();
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Role helpers
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CEO: 'ceo',
  CENTRAL_TEAM: 'central_team',
  BUSINESS_HEAD: 'business_head',
  BUSINESS_MANAGER: 'business_manager',
  REGIONAL_HEAD: 'regional_head',
  CLUSTER_MANAGER: 'cluster_manager',
  CLUSTER_AUDIO_HEAD: 'cluster_audio_head',
  CLINIC_MANAGER: 'clinic_manager',
  CLINIC_HEAD: 'clinic_head',
  CLINIC_PC: 'clinic_pc',
  FRONT_DESK: 'front_desk',
  AUDIOLOGIST: 'audiologist',
  ENT_DOCTOR: 'ent_doctor',
  CENTRAL_SCHEDULER: 'central_scheduler',
};

const ROLE_ORDER = [
  ROLES.AUDIOLOGIST, ROLES.CLINIC_PC, ROLES.FRONT_DESK, ROLES.ENT_DOCTOR, ROLES.CENTRAL_SCHEDULER,
  ROLES.CLINIC_HEAD, ROLES.CLINIC_MANAGER,
  ROLES.CLUSTER_AUDIO_HEAD, ROLES.CLUSTER_MANAGER,
  ROLES.BUSINESS_MANAGER, ROLES.REGIONAL_HEAD,
  ROLES.BUSINESS_HEAD, ROLES.CENTRAL_TEAM, ROLES.CEO, ROLES.SUPER_ADMIN,
];

export const roleLevel = (r) => ROLE_ORDER.indexOf(r);
export const canManage = (role) => roleLevel(role) >= roleLevel(ROLES.CLINIC_MANAGER);
export const isAdmin = (role) => role === ROLES.SUPER_ADMIN || role === ROLES.CEO;

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  ceo: 'CEO',
  central_team: 'Central Team',
  business_head: 'Business Head',
  business_manager: 'Business Manager',
  regional_head: 'Regional Head',
  cluster_manager: 'Cluster Manager',
  cluster_audio_head: 'Cluster Audio Head',
  clinic_manager: 'Clinic Manager',
  clinic_head: 'Clinic Head',
  clinic_pc: 'Clinic PC',
  front_desk: 'Front Desk',
  audiologist: 'Audiologist',
  ent_doctor: 'ENT Doctor',
  central_scheduler: 'Central Scheduler',
};
